import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import companyService from '../services/companyService';
import { setApiBaseUrl, tokenManager } from '../config/apiConfig';

const CompanyContext = createContext();

const STORAGE_KEY = 'dispatchCompany';

export const useCompany = () => {
  const context = useContext(CompanyContext);
  if (!context) throw new Error('useCompany must be used within a CompanyProvider');
  return context;
};

export const CompanyProvider = ({ children }) => {
  const [company, setCompany] = useState(null);
  const [companyCode, setCompanyCode] = useState(null);
  const [isResolved, setIsResolved] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true); // true until localStorage check completes
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // On mount, restore persisted company from localStorage
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const { code, data } = JSON.parse(saved);
        setCompanyCode(code);
        setCompany(data);
        setApiBaseUrl(data.apiUrl);
        tokenManager.setCompanyCode(code);
        setIsResolved(true);
      } catch {
        localStorage.removeItem(STORAGE_KEY);
      }
    }
    setIsInitializing(false);
  }, []);

  const setCompanyByCode = useCallback(async (code) => {
    if (!code) return null;
    setIsLoading(true);
    setError(null);

    try {
      const data = await companyService.resolveCompany(code);
      if (!data) {
        setError(`Company "${code}" not found`);
        setIsLoading(false);
        return null;
      }

      setCompanyCode(code);
      setCompany(data);
      setApiBaseUrl(data.apiUrl);
      tokenManager.setCompanyCode(code);
      setIsResolved(true);
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ code, data }));
      setIsLoading(false);
      return data;
    } catch (err) {
      setError(err.message || 'Failed to resolve company');
      setIsLoading(false);
      return null;
    }
  }, []);

  const clearCompany = useCallback(() => {
    setCompany(null);
    setCompanyCode(null);
    setIsResolved(false);
    setApiBaseUrl(null);
    tokenManager.setCompanyCode(null);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  return (
    <CompanyContext.Provider value={{ company, companyCode, isResolved, isInitializing, isLoading, error, setCompanyByCode, clearCompany }}>
      {children}
    </CompanyContext.Provider>
  );
};

export default CompanyContext;
